import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import PageLoader from '../common/PageLoader';
import ErrorBoundary from '../common/ErrorBoundary';
import Feedback from '../common/Feedback';
import { setUser } from '../../store/userSlice';
import { setCourseId } from '../../store/courseSlice';

/*
 * LtiCallback is the auth gate for all Canvas-embedded content.
 *
 * It validates the LTI session, stores the user in Redux, then renders
 * the canvas feature via Outlet. Until validation completes it shows a
 * spinner; on failure, an error.
 */
function LtiCallback() {
  const { session } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const hasValidated = useRef(false);

  useEffect(() => {
    if (hasValidated.current) return;
    hasValidated.current = true;

    const validate = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/mss/internal/lti/validate/${session}`,
        );
        dispatch(setUser(data));
        dispatch(setCourseId(data.courseId));
        setIsReady(true);
      } catch {
        setError('Session validation failed. Please relaunch from Canvas.');
      }
    };
    validate();
  }, [session, dispatch]);

  if (error) {
    return (
      <Box className="mss__centeredScreen mss__ltiCallback">
        <Typography className="ltiCallbackError">{error}</Typography>
      </Box>
    );
  }

  if (!isReady) {
    return (
      <Box className="mss__centeredScreen mss__ltiCallback">
        <PageLoader />
      </Box>
    );
  }

  // Renders the canvas features matched under /lti/:session
  return (
    <ErrorBoundary onGoToDashboard={() => navigate(`/lti/${session}`, { replace: true })}>
      <Outlet />
      {/* Offered from every Canvas page, rather than left to each to remember. */}
      <Feedback id="ltiFeedback" />
    </ErrorBoundary>
  );
}

export default LtiCallback;
